import { StaySortOption } from '../../../__generated__/graphql';
import type { IStayCardFieldsFragment } from '../../services/stayService/__generated__/queries.generated';
import { DEFAULT_GUESTS, EMPTY_FILTERS, type StaySearchState } from './staySearchState';

export const EMPTY_SEARCH: StaySearchState = {
  place: '',
  guests: DEFAULT_GUESTS,
  filters: EMPTY_FILTERS,
  sortBy: StaySortOption.Recommended,
};

type Item = { listing: IStayCardFieldsFragment };

export interface HomeRow {
  key: string;
  title: string;
  items: Item[];
  /** Where "Popular homes in Boulder ›" leads. */
  search?: StaySearchState;
}

/**
 * Home rows, Airbnb style: "Popular homes in <town>" for each town with at
 * least three places (most places first, at most four towns), then "Guest
 * favorites". Fewer than two rows means there is not enough to sort into rows,
 * and the page shows one grid instead.
 */
export function homeRows(items: Item[]): HomeRow[] {
  const towns = new Map<string, Item[]>();
  items.forEach(item => {
    const city = item.listing.location?.city;
    if (!city) return;
    const key = [city, item.listing.location?.state].filter(Boolean).join(', ');
    towns.set(key, [...(towns.get(key) ?? []), item]);
  });
  const rows: HomeRow[] = Array.from(towns.entries())
    .filter(([, list]) => list.length >= 3)
    .sort((a, b) => b[1].length - a[1].length)
    .slice(0, 4)
    .map(([town, list]) => {
      const point = list[0].listing.approximateLocation;
      return {
        key: `town-${town}`,
        title: `Popular homes in ${town.split(',')[0]}`,
        items: list,
        search: point
          ? { ...EMPTY_SEARCH, place: town, latitude: point.latitude, longitude: point.longitude }
          : undefined,
      };
    });
  const favorites = items.filter(
    item => Number(item.listing.averageRating) >= 4.8 && (item.listing.totalRatings ?? 0) >= 3,
  );
  if (favorites.length >= 3) {
    rows.push({ key: 'favorites', title: 'Guest favorites', items: favorites });
  }
  return rows.length >= 2 ? rows : [];
}

/** "Over 1,000 places", "12 places in Boulder" (Airbnb wording). */
export function resultsHeading(total: number, place: string, inMapArea: boolean): string {
  const count =
    total > 1000 ? 'Over 1,000 places' : `${total.toLocaleString()} place${total === 1 ? '' : 's'}`;
  if (inMapArea) return `${count} within map area`;
  return place ? `${count} near ${place}` : count;
}
